import React, { createContext, useContext, useMemo } from "react";
import { useVideoConfig } from "remotion";

/**
 * Output formats of the B2B video. Same timeline everywhere; only the
 * layout adapts.
 *
 *   landscape  1920×1080  partner page embed, LinkedIn desktop, YouTube (master)
 *   feed       1080×1350  LinkedIn / Instagram mobile feed (4:5, most screen)
 *   square     1080×1080  LinkedIn / Facebook feed, decks, WhatsApp
 */
export type B2BFormat = "landscape" | "feed" | "square";

export const B2B_SIZES: Record<B2BFormat, { width: number; height: number }> = {
  landscape: { width: 1920, height: 1080 },
  feed: { width: 1080, height: 1350 },
  square: { width: 1080, height: 1080 },
};

/** Scenes are authored on a stage whose short side is always 1080. */
const UNIT = 1080;

export type Stage = {
  /** Logical stage size (short side = 1080). */
  w: number;
  h: number;
  /** Layout family: scenes branch on this, never on the raw format. */
  shape: "wide" | "square" | "tall";
};

const StageContext = createContext<Stage | null>(null);

export const useStage = (): Stage => {
  const stage = useContext(StageContext);
  if (!stage) throw new Error("useStage() must be used inside <StageFrame>.");
  return stage;
};

/**
 * Lays out a logical stage (short side 1080) and scales it to the real
 * composition size. Every format currently renders unscaled (k = 1), the
 * frame just keeps the door open for other sizes.
 */
export const StageFrame: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { width, height } = useVideoConfig();
  const k = Math.min(width, height) / UNIT;
  const w = width / k;
  const h = height / k;

  const stage = useMemo<Stage>(
    () => ({ w, h, shape: w === h ? "square" : w > h ? "wide" : "tall" }),
    [w, h],
  );

  return (
    <StageContext.Provider value={stage}>
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: w,
          height: h,
          transform: k === 1 ? undefined : `scale(${k})`,
          transformOrigin: "0 0",
        }}
      >
        {children}
      </div>
    </StageContext.Provider>
  );
};
